import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Music2, Loader2, Trophy, ShieldCheck, Zap, Trash2, X } from "lucide-react";
import { royai, fmtUsd } from "@/lib/royai";
import { useLiveEvents } from "@/lib/liveEvents";

/* Section 08 — Verified Portfolio
 * Grid of cards for every audited track in RoyAI. Each card shows artwork,
 * title, main artist, role label, and lifetime verified streams + revenue.
 * "Infrastructure Verified" badge appears when the user has approved metadata
 * + royalty evidence covering the track (plus rights protection at account
 * level). Each card links back to the track's Deep Dive in RoyAI. */
export default function VerifiedPortfolio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("finalized"); // finalized | all

  const load = async () => {
    try {
      const d = await royai.portfolio();
      setData(d);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useLiveEvents(["portfolio.finalized", "audit.updated"], load);

  if (loading) {
    return (
      <div className="p-8 bg-[#0c0c0c] border border-white/5 border-t-0 flex items-center gap-2 text-neutral-500 text-sm">
        <Loader2 size={14} className="animate-spin" /> Loading your portfolio…
      </div>
    );
  }
  if (!data || data.totals.track_count === 0) {
    return (
      <div className="p-8 bg-[#0c0c0c] border border-white/5 border-t-0" data-testid="portfolio-empty">
        <div className="flex items-center gap-3 mb-2">
          <Music2 size={18} className="text-amber-500" />
          <div className="text-overline">Verified Portfolio</div>
        </div>
        <p className="text-sm text-neutral-400 max-w-xl">
          Nothing audited yet. Run a RoyAI audit or paste a link/screenshot in RoyAI, then hit
          <span className="text-amber-500"> Finalize &amp; Add to Portfolio </span>
          to lock your discography in as a permanent record here.
        </p>
        <Link
          to="/royai"
          data-testid="portfolio-empty-cta"
          className="inline-flex items-center gap-2 mt-4 bg-amber-500 text-black font-semibold px-5 py-2.5 text-sm hover:brightness-110 transition-all"
        >
          Open RoyAI <ExternalLink size={14} />
        </Link>
      </div>
    );
  }

  const finalized = data.tracks.filter((t) => t.portfolio_finalized);
  const shown = filter === "finalized" && finalized.length > 0 ? finalized : data.tracks;

  return (
    <div className="border border-white/5 border-t-0" data-testid="verified-portfolio">
      {/* Header row */}
      <div className="bg-[#0A0A0A] p-6 lg:p-8 border-b border-white/5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Trophy size={16} className="text-amber-500" />
              <div className="text-overline">Your Discography</div>
            </div>
            <h3 className="font-display text-xl lg:text-2xl font-medium text-white tracking-tight">
              {data.totals.track_count} tracks · {data.totals.lifetime_streams.toLocaleString()} lifetime plays
            </h3>
            <div className="text-sm text-neutral-400 mt-1">
              {fmtUsd(data.totals.revenue_audited_usd)} revenue audited ·
              <span className="text-amber-500"> {fmtUsd(data.totals.your_share_usd)} your share</span>
              {data.totals.verified_count > 0 && (
                <> · <span className="text-emerald-400">{data.totals.verified_count} verified</span></>
              )}
              {data.totals.shazam_count > 0 && (
                <> · <span className="text-sky-300">{data.totals.shazam_count.toLocaleString()} Shazams</span></>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => setFilter("finalized")}
              data-testid="portfolio-filter-finalized"
              className={`px-3 py-1.5 uppercase tracking-widest font-semibold ${
                filter === "finalized"
                  ? "bg-amber-500 text-black"
                  : "border border-white/10 text-neutral-400 hover:text-white"
              }`}
            >
              Finalized ({finalized.length})
            </button>
            <button
              onClick={() => setFilter("all")}
              data-testid="portfolio-filter-all"
              className={`px-3 py-1.5 uppercase tracking-widest font-semibold ${
                filter === "all"
                  ? "bg-amber-500 text-black"
                  : "border border-white/10 text-neutral-400 hover:text-white"
              }`}
            >
              All ({data.totals.track_count})
            </button>
          </div>
        </div>
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        {shown.map((t) => (
          <PortfolioCard key={t.track_id} track={t} onDeleted={load} />
        ))}
      </div>

      {finalized.length === 0 && filter === "finalized" && (
        <div className="bg-[#0A0A0A] p-6 text-center text-sm text-neutral-500 border-t border-white/5">
          None finalized yet — open RoyAI and hit&nbsp;
          <Link to="/royai" className="text-amber-500 hover:underline">Finalize &amp; Add to Portfolio</Link>
          &nbsp;to lock in your audited catalog.
        </div>
      )}
    </div>
  );
}

function PortfolioCard({ track, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const plays = track.lifetime_total_plays;
  const initials = (track.main_artist || track.title || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await royai.deleteTrack(track.track_id);
      toast.success(
        `Removed "${track.display_title || track.title}"` +
          (res?.cascade ? ` · ${res.cascade.revenue_rows} rows, ${res.cascade.bounties} bounties cleared` : "")
      );
      if (onDeleted) onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete track");
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  };

  return (
    <div
      className="bg-[#0c0c0c] p-5 flex flex-col gap-3 relative group"
      data-testid={`portfolio-card-${track.track_id}`}
    >
      {/* Delete button — hovers top-right of the card; two-step confirm */}
      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          data-testid={`portfolio-delete-${track.track_id}`}
          title="Remove from Portfolio"
          className="absolute top-2 left-2 z-20 p-1.5 bg-black/70 border border-white/10 text-neutral-500 hover:text-red-400 hover:border-red-400/40 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
        >
          <Trash2 size={12} />
        </button>
      ) : (
        <div
          className="absolute inset-x-2 top-2 z-20 bg-red-950/95 border border-red-500/50 px-3 py-2 flex items-center gap-2 text-[11px]"
          data-testid={`portfolio-delete-confirm-${track.track_id}`}
        >
          <span className="text-red-200 flex-1">Delete this track?</span>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            data-testid={`portfolio-delete-yes-${track.track_id}`}
            className="px-2 py-1 bg-red-500 text-white font-semibold uppercase tracking-widest text-[10px] hover:brightness-110 disabled:opacity-50"
          >
            {deleting ? <Loader2 size={10} className="animate-spin" /> : "Yes"}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={deleting}
            className="px-2 py-1 border border-white/15 text-neutral-300 uppercase tracking-widest text-[10px] hover:text-white"
          >
            <X size={10} />
          </button>
        </div>
      )}
      {/* Artwork or initials block */}
      <div className="aspect-square w-full bg-gradient-to-br from-[#161616] to-[#050505] border border-white/5 flex items-center justify-center overflow-hidden relative">
        {track.artwork_url ? (
          <img
            src={track.artwork_url}
            alt={track.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <span className="font-display text-5xl text-amber-500/60 tracking-tighter">{initials}</span>
        )}
        {track.infrastructure_verified && (
          <span
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-widest font-semibold"
            data-testid={`portfolio-verified-badge-${track.track_id}`}
          >
            <ShieldCheck size={10} /> Infrastructure Verified
          </span>
        )}
        {track.portfolio_finalized && !track.infrastructure_verified && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] uppercase tracking-widest font-semibold">
            <CheckCircle2 size={10} /> Finalized
          </span>
        )}
      </div>

      {/* Text */}
      <div>
        <div className="font-display text-lg font-medium text-white leading-tight truncate">
          {track.display_title || track.title}
        </div>
        <div className="text-xs text-neutral-500 mt-0.5 truncate">
          {track.main_artist || "Unknown artist"}
        </div>
        <div className="text-[11px] uppercase tracking-widest text-amber-500/80 mt-1">
          {track.role_label ? `${track.role_label} · ` : ""}
          {track.isrc || "ISRC pending"}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/5 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-600">Lifetime Plays</div>
          <div className="font-mono-tech text-sm text-white">{plays.toLocaleString()}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-600">Revenue Audited</div>
          <div className="font-mono-tech text-sm text-amber-500">{fmtUsd(track.revenue_audited_usd)}</div>
        </div>
      </div>

      {/* Shazam indicator — discovery signal (not revenue) */}
      {track.shazam_count > 0 && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-[#050505] border border-sky-400/20 text-[11px]" data-testid={`portfolio-shazam-${track.track_id}`}>
          <div className="flex items-center gap-1.5 text-sky-300">
            <Zap size={11} />
            <span className="font-mono-tech">{track.shazam_count.toLocaleString()} Shazams</span>
          </div>
          <span className="text-neutral-500">
            ~{fmtUsd(track.shazam_market_value_usd)} market value
          </span>
        </div>
      )}

      {/* Deep Dive link */}
      <Link
        to={`/royai?track=${track.track_id}#track-${track.track_id}`}
        data-testid={`portfolio-deepdive-${track.track_id}`}
        className="mt-1 inline-flex items-center justify-center gap-2 border border-amber-500/40 text-amber-400 hover:bg-amber-500 hover:text-black transition-all px-3 py-2 text-xs uppercase tracking-widest font-semibold"
      >
        View Full RoyAI Audit <ExternalLink size={12} />
      </Link>
    </div>
  );
}
